import { companyPath, contactPath } from 'helper/recordPaths';
import { CalendarDaysIcon, CheckIcon, ClockIcon, ExternalLink, MapPinIcon, Pencil, PhoneIcon, TrashIcon } from 'assets/icons';
import CardIconButton from 'components/card/CardIconButton';
import { useDial } from 'components/record/callAction';
import ActivityTimeline from 'components/activity/ActivityTimeline';
import ACTIVITY_ICONS from 'components/activity/activityIcons';
import activitySentence from 'components/activity/activitySentence';
import EmployeeAvatar from 'components/assign/EmployeeAvatar';
import Badge from 'components/badge/badge';
import Button from 'components/button';
import CommentComposer from 'components/comments/CommentComposer';
import CommentThread from 'components/comments/CommentThread';
import Spinner from 'components/crm/ui/Spinner';
import SummaryCell from 'components/crm/ui/SummaryCell';
import AppointmentDetailSkeleton from 'components/planning/panel/AppointmentDetailSkeleton';
import { TaskActivitySkeleton, TaskCommentsSkeleton } from 'components/tasks/panel/TaskPanelSkeletons';
import useCommentThread from 'components/comments/useCommentThread';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverTabs from 'components/SlideOver/component/tabs';
import SlideOverFooter from 'components/SlideOver/component/footer';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import AutosaveTextarea from 'components/styledInput/AutosaveTextarea';
import { eventCategoryOf, eventCategoryText } from 'constants/eventCategories';
import { describeRecurrence, languageCode } from 'helper/recurrence';
import { addDays, clockLabel, dayLabel, fromIsoDay, isoDay } from 'helper/timeLabels';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import { useNotifyPlanningChanged } from 'hooks/usePlanningChanged';
import useText from 'hooks/useText';
import { CircleDashed, Repeat, X as XIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiErrorText } from 'helper/apiErrorText';
import crmApi from 'server/crm/crmApi';
import AppointmentForm, { categoryWayOf } from './AppointmentForm';
import NotDoneForm, { notDoneReasonText } from './NotDoneForm';
import ScopeDialog from './ScopeDialog';
import AppointmentGoogleDetails from './panel/AppointmentGoogleDetails';
import AppointmentResponseBar from './panel/AppointmentResponseBar';

// A real company of Jobbiz has a page; the demo's own do not.
const isRealId = (id) => /^[0-9a-f]{24}$/i.test(id ?? '');

const nameOf = (person) => [person?.firstname, person?.lastname].filter(Boolean).join(' ');

// The rule of an appointment's series in words; « Se répète » while the
// series of a Google event is not read yet.
const seriesRuleOf = (series, recurrenceText, language) => {
  if (!series) return recurrenceText('repeats');
  if (!series.rule) return recurrenceText('googleCustom');
  return describeRecurrence(series.rule, isoDay(new Date(series.startsAt)), languageCode(language));
};

// « Aujourd'hui, 9:00 – 10:00 », « Mar. 27 oct., Toute la journée », or over
// several days « Du lun. 26 oct. au mer. 28 oct. ». A whole day's endDay is
// the day after its last one.
const whenOf = (item, start, end, now, text) => {
  const first = item.allDay && item.startDay ? fromIsoDay(item.startDay) : start;
  const last = item.allDay && item.endDay ? addDays(fromIsoDay(item.endDay), -1) : end;
  if (isoDay(first) !== isoDay(last)) {
    return item.allDay
      ? text('fromTo', { from: dayLabel(first), to: dayLabel(last) })
      : text('fromTo', { from: `${dayLabel(first)}, ${clockLabel(start)}`, to: `${dayLabel(last)}, ${clockLabel(end)}` });
  }
  let day = dayLabel(first);
  if (isoDay(first) === isoDay(now)) day = text('today');
  else if (isoDay(first) === isoDay(addDays(now, 1))) day = text('tomorrow');
  else if (isoDay(first) === isoDay(addDays(now, -1))) day = text('yesterday');
  return `${day}, ${item.allDay ? text('allDay') : `${clockLabel(start)} – ${clockLabel(end)}`}`;
};

/**
 * The panel of an appointment (slide-over `get_appointment`, its id in
 * `objID`), as on the prototype: its category, when, whether it was held,
 * the person met, where, its report written in place; its history and its
 * comments. « Réalisé » or « Non réalisé » with its reason (NotDoneForm, in place of the detail), at
 * any hour; called while not over. Its owner
 * or its creator (access.canEdit) changes its status, its report, deletes it;
 * they edit its title, location, slot and description in place of the detail
 * (AppointmentForm) unless Google Calendar forbids it
 * (access.canEditDetails). An occurrence of a series reads its rule under its
 * slot, and its delete asks for its scope (ScopeDialog). Every change tells the
 * planning (usePlanningChanged).
 */
const AppointmentPanel = () => {
  const { objID, setObjID } = UseContext();
  const id = typeof objID === 'string' ? objID : objID?.appointmentId ?? null;
  // Opened to be moved (« Reporter »): its form first, when it may be edited.
  const editFirst = Boolean(objID?.edit);
  const { text, language } = useText('appointment');
  const dial = useDial();
  const { call, me } = useCrmContext();
  const { toggleSlideOver } = useSlideOver();
  const navigate = useNavigate();
  const location = useLocation();
  const notify = useNotifyPlanningChanged();
  const { text: tasksText } = useText('tasks');
  const { text: planningText } = useText('planning');
  const { text: recurrenceText } = useText('recurrence');
  const [item, setItem] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [team, setTeam] = useState([]);
  const [tab, setTab] = useState('detail');
  const [editing, setEditing] = useState(false);
  const [deleteScopeOpen, setDeleteScopeOpen] = useState(false);
  const [notDoneOpen, setNotDoneOpen] = useState(false);
  // The action under way ('held', 'delete'…), or null. The ref ignores a
  // second click before the next render; the state shows « Suppression… » or
  // the like on its button.
  const [running, setRunning] = useState(null);
  const runningRef = useRef(null);

  const load = useCallback(() => call(crmApi.getAppointment, { id }).then(setItem, (error) => {
        setItem(null);
        setLoadError(apiErrorText(error, { language, text: tasksText, group: 'errors' }) ?? '');
      }),
    [call, id, language, tasksText],
  );
  useEffect(() => {
    if (!id || !me) return;
    setItem(null);
    setLoadError('');
    setTab('detail');
    setEditing(editFirst);
    setDeleteScopeOpen(false);
    setNotDoneOpen(false);
    load();
    call(crmApi.getTeam).then(setTeam, () => setTeam([]));
  }, [call, load, id, me, editFirst]);

  const thread = useCommentThread({
    // The comments of a private appointment of someone else are refused: none loaded.
    key: item && !item.masked ? id : null,
    load: () => call(crmApi.listAppointmentComments, { id }),
    add: (body, mentions) => call(crmApi.addAppointmentComment, { id, body, mentions }),
    update: (commentId, body, mentions) => call(crmApi.updateAppointmentComment, { id, commentId, body, mentions }),
    remove: (commentId) => call(crmApi.deleteAppointmentComment, { id, commentId }),
  });

  const close = () => toggleSlideOver();
  if (loadError && !item) {
    return (
      <>
        <SlideOverHeader hide={close} closeLabel={text('close')} />
        <SlideOverBody>
          <p role="alert" className="mt-16 text-center text-sm text-gray-600">
            {loadError}
          </p>
        </SlideOverBody>
      </>
    );
  }
  // Its title and its tabs' content in grey until it lands; the tabs are there.
  if (!item) {
    return (
      <>
        <SlideOverHeader hide={close} closeLabel={text('close')}>
          <span className="block h-7 w-2/3 animate-pulse rounded bg-gray-200" />
        </SlideOverHeader>
        <SlideOverTabs
          activeTab={tab}
          onChange={setTab}
          tabs={[
            { id: 'detail', label: text('tabDetail') },
            { id: 'activity', label: text('tabActivity') },
            { id: 'comments', label: text('tabComments') },
          ]}
        />
        <SlideOverBody className="flex flex-col">
          {tab === 'detail' && <AppointmentDetailSkeleton />}
          {tab === 'activity' && <TaskActivitySkeleton />}
          {tab === 'comments' && <TaskCommentsSkeleton />}
        </SlideOverBody>
      </>
    );
  }

  const now = new Date();
  const start = new Date(item.startsAt);
  const end = new Date(item.endsAt);
  // An appointment « à placer » has no slot yet: it is placed by editing it.
  const toPlace = item.status === 'to_place';
  const when = toPlace ? 'À placer' : whenOf(item, start, end, now, text);
  // A private appointment of someone else: its slot and its owner only.
  if (item.masked) {
    return (
      <>
        <SlideOverHeader hide={close} closeLabel={text('close')}>
          {text('busy')}
        </SlideOverHeader>
        <SlideOverBody className="flex flex-col">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge type="PRIMARY">
                <ClockIcon wh={14} color="currentColor" />
                {when}
              </Badge>
            </div>
            {item.owner && (
              <p className="flex items-center gap-2 text-sm text-gray-700">
                <span className="flex shrink-0 text-gray-400">
                  <CalendarDaysIcon wh={16} color="currentColor" />
                </span>
                {text('calendarOf', { name: nameOf(item.owner) })}
              </p>
            )}
          </div>
        </SlideOverBody>
      </>
    );
  }
  const company = item.company;
  const category = eventCategoryOf(item.category);
  const contact = item.contactName || company?.contactName || '';
  const role = contact && contact === company?.contactName ? company?.contactRole : '';
  // The person met opens on their contact page: the one chosen, else the
  // company's contact the api put in its place.
  const contactId = item.contactId || (contact && contact === company?.contactName ? company?.contactId : null);
  const phone = company?.phone ?? '';
  const late = end < now;
  // `message` may read the answer: a deletion says whether its visit is back to place.
  // `key` names the action: one at a time, its button reads `<key>Pending` meanwhile.
  // `stay`: the panel stays open on the appointment, read again (a change of
  // its status: « Réalisé », « Non réalisé », « Rouvrir »).
  const act = async (run, message, key, { stay = false } = {}) => {
    if (runningRef.current) return;
    runningRef.current = key;
    setRunning(key);
    try {
      const result = await run();
      toast.success(typeof message === 'function' ? message(result) : message);
      notify();
      if (stay) await load();
      else close();
    } catch (error) {
      const message = apiErrorText(error, { language, text: tasksText, group: 'errors' });
      if (message) toast.error(message);
    } finally {
      runningRef.current = null;
      setRunning(null);
    }
  };
  const actionText = (key) => text(running === key ? `${key}Pending` : key);
  // The answer of a guest: the appointment is read again, its access and its guests with it.
  const respond = (response) =>
    call(crmApi.respondAppointment, { id: item.id, response }).then(
      () => {
        notify();
        return load();
      },
      (error) => {
        const message = apiErrorText(error, { language, text: tasksText, group: 'errors' });
        if (message) toast.error(message);
      },
    );
  const mark = (status, message, key, options) =>
    act(() => call(crmApi.updateAppointment, { id: item.id, fields: { status } }), message, key, options);
  const markNotDone = (notDoneReason, notDoneDetail) =>
    act(
      () => call(crmApi.updateAppointment, { id: item.id, fields: { status: 'no_show', notDoneReason, notDoneDetail } }),
      text('notDoneSaved'),
      'notDone',
      { stay: true },
    );
  // An appointment marked not held before reasons existed reads « Non réalisé » alone.
  const notDoneLabel = (reason, detail) => {
    const why = notDoneReasonText(reason, detail, text);
    return why ? text('statusNotDone', { reason: why }) : text('statusNoShow');
  };
  const saveReport = (outcome) =>
    call(crmApi.updateAppointment, { id: item.id, fields: { outcome } }).then(
      () => {
        toast.success(text('reportSaved'));
        notify();
        return true;
      },
      (error) => {
        const message = apiErrorText(error, { language, text: tasksText, group: 'errors' });
        if (message) toast.error(message);
        return false;
      },
    );
  const [firstName, ...rest] = contact.split(' ');
  const canEdit = item.access?.canEdit !== false;
  // A visit's one reads « Supprimer le rendez-vous » too; the api still puts
  // the visit back to place, and the toast says so.
  const removeKey = 'delete';
  const canEditDetails = canEdit && item.access?.canEditDetails !== false;
  const editable = item.status === 'planned' || toPlace;
  const editingDetails = editing && canEditDetails && editable;
  const owner = item.owner?.id && item.owner.id !== me?.id ? item.owner : null;
  // A step without an actor comes from the synchronisation with Google Calendar.
  // Its icon says what it did, as in a task's activity.
  const HISTORY_ICONS = {
    ...ACTIVITY_ICONS,
    no_show: ACTIVITY_ICONS.canceled,
    planned: ACTIVITY_ICONS.reopened,
  };
  // One sentence a step, as the user reads it: « Vous avez créé ce rendez-vous »,
  // « Dominique a marqué ce rendez-vous comme effectué »; a step without anybody comes from Google.
  const HISTORY_PHRASES = {
    created: () => 'créé ce rendez-vous',
    done: () => 'marqué ce rendez-vous comme effectué',
    no_show: (event) => {
      const why = notDoneReasonText(event.notDoneReason, event.notDoneDetail, text);
      return `marqué ce rendez-vous comme non réalisé${why ? ` : ${why}` : ''}`;
    },
    canceled: () => 'annulé ce rendez-vous',
    planned: () => 'rouvert ce rendez-vous',
    updated: () => 'modifié ce rendez-vous',
  };
  const history = (item.history ?? []).map((event) => ({
    id: event.id,
    icon: HISTORY_ICONS[event.kind] ?? HISTORY_ICONS.updated,
    text: activitySentence(event.actor, me?.id, (HISTORY_PHRASES[event.kind] ?? HISTORY_PHRASES.updated)(event), text('googleCalendar')),
    at: new Date(event.at),
  }));

  let actions = null;
  // One still to place has no status to change: « Modifier » gives it its date.
  if (!canEdit || toPlace) actions = null;
  else if (item.status !== 'planned') {
    actions = (
      <Button color="SECONDARY" size="SM" disabled={Boolean(running)} onClick={() => mark('planned', text('reopened'), 'reopen', { stay: true })}>
        {actionText('reopen')}
      </Button>
    );
  } else {
    actions = (
      <>
        <Button color="SECONDARY" size="SM" disabled={Boolean(running)} onClick={() => setNotDoneOpen(true)}>
          {text('notDone')}
        </Button>
        <Button color="PRIMARY" size="SM" disabled={Boolean(running)} onClick={() => mark('done', text('heldDone'), 'held', { stay: true })}>
          {actionText('held')}
        </Button>
      </>
    );
  }

  const CategoryIcon = categoryWayOf(category).icon;
  // Where it stands: held, not held (and why), canceled, or still to hold.
  const state =
    {
      done: { icon: <CheckIcon w={14} h={11} color="currentColor" />, label: text('statusDone'), tone: 'text-green-700' },
      no_show: { icon: <XIcon size={16} aria-hidden="true" />, label: notDoneLabel(item.notDoneReason, item.notDoneDetail), tone: 'text-red-600' },
      canceled: { icon: <XIcon size={16} aria-hidden="true" />, label: 'Annulé', tone: 'text-gray-500' },
    }[item.status] ?? { icon: <CircleDashed size={16} aria-hidden="true" />, label: 'Pas encore réalisé', tone: undefined };
  const lateAndPlanned = late && item.status === 'planned';
  const header = (
    <SlideOverHeader hide={close} closeLabel={text('close')}>
      {(category === 'interne' ? item.title : company?.name || item.title) || planningText('untitled')}
    </SlideOverHeader>
  );
  // « Non réalisé »: its reason and its note take the slide-over, as an edit does.
  if (notDoneOpen) {
    return (
      <>
        <SlideOverHeader
          hide={close}
          closeLabel={text('close')}
          description="Indiquez pourquoi le rendez-vous n’a pas eu lieu : le motif, puis ce qui s’est passé."
        >
          {text('notDoneTitle')}
        </SlideOverHeader>
        <NotDoneForm
          onCancel={() => setNotDoneOpen(false)}
          onConfirm={(reason, detail) => markNotDone(reason, detail).finally(() => setNotDoneOpen(false))}
        />
      </>
    );
  }

  return (
    <>
      {header}
      <SlideOverTabs
        activeTab={tab}
        onChange={setTab}
        tabs={[
          { id: 'detail', label: text('tabDetail') },
          { id: 'activity', label: text('tabActivity') },
          { id: 'comments', label: text('tabComments') },
        ]}
      />
      <SlideOverBody className="flex flex-col">
        {tab === 'comments' && (
          <CommentThread
            comments={thread.comments}
            people={team}
            meId={me?.id}
            onEdit={thread.edit}
            onDelete={thread.removeComment}
            pendingIds={thread.pendingIds}
          />
        )}
        {tab === 'activity' && <ActivityTimeline items={history} />}
        {tab === 'detail' && editingDetails && (
          <AppointmentForm
            mode="edit"
            item={item}
            calendarSynced={Boolean(item.calendarSynced)}
            onCancel={() => setEditing(false)}
            onSaved={(updated) => {
              setEditing(false);
              notify();
              // A change of the following events may answer another
              // occurrence (the first of a new series): the panel opens it.
              if (updated?.id && updated.id !== item.id && setObjID) {
                setObjID(updated.id);
                return;
              }
              // The demo answers without history nor access: keep the loaded ones.
              setItem((current) => ({ ...current, ...updated }));
            }}
          />
        )}
        {tab === 'detail' && !editingDetails && (
          <div className="flex flex-col gap-4">
            {/* Type and date side by side, its place and its state under them. */}
            <div className="grid grid-cols-2 rounded-xl border border-gray-200">
              <SummaryCell
                icon={<CategoryIcon size={16} aria-hidden="true" />}
                label="Type"
                value={eventCategoryText(language, category, 'label')}
                className="border-r border-gray-200"
              />
              <SummaryCell
                icon={<ClockIcon wh={16} color="currentColor" />}
                iconClass={lateAndPlanned ? 'text-red-600' : undefined}
                label="Date"
                value={when}
                valueClass={lateAndPlanned ? 'text-red-600' : undefined}
              />
              {item.location && (
                <SummaryCell
                  icon={<MapPinIcon wh={16} color="currentColor" />}
                  label="Lieu"
                  value={item.location}
                  wrap
                  className="border-r border-t border-gray-200"
                />
              )}
              <SummaryCell
                icon={state.icon}
                iconClass={state.tone}
                label="État"
                value={state.label}
                valueClass={state.tone}
                className={`border-t border-gray-200 ${item.location ? '' : 'col-span-2'}`}
              />
            </div>
            {item.recurring && (
              <p className="flex items-center gap-2 text-sm text-gray-700">
                <span className="flex shrink-0 text-gray-400">
                  <Repeat size={16} aria-hidden="true" />
                </span>
                {seriesRuleOf(item.series, recurrenceText, language)}
              </p>
            )}
            {owner && (
              <p className="flex items-center gap-2 text-sm text-gray-700">
                <span className="flex shrink-0 text-gray-400">
                  <CalendarDaysIcon wh={16} color="currentColor" />
                </span>
                {text('calendarOf', { name: nameOf(owner) })}
              </p>
            )}
            {(contact || company) && (
              <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-3">
                <EmployeeAvatar employee={{ firstname: firstName || company?.name, lastname: rest.join(' ') }} size={40} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900">{contact || company?.name}</p>
                  <p className="text-xs text-gray-600">{[role, phone].filter(Boolean).join(' · ')}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {/* The number shown, called through Wildix as from a record page. */}
                  {phone && <CardIconButton label={text('call')} icon={<PhoneIcon wh={18} color="#374151" />} onClick={() => dial(phone)} />}
                  {(isRealId(contactId) || (company && isRealId(company.id))) && (
                    <CardIconButton
                      label={text('record')}
                      icon={<ExternalLink wh={18} color="#374151" />}
                      onClick={() => {
                        // The page opens in place of the panel.
                        close();
                        navigate(isRealId(contactId) ? contactPath(contactId) : companyPath(company.id));
                      }}
                    />
                  )}
                </div>
              </div>
            )}
            <AppointmentGoogleDetails item={item} />
            <AutosaveTextarea
              key={item.id}
              label={text('report')}
              placeholder={text('reportPlaceholder')}
              initial={item.outcome ?? ''}
              onSave={saveReport}
              isDisabled={!canEdit}
            />
            {item.description && (
              <div>
                <p className="text-sm font-medium text-gray-700">{text('note')}</p>
                <p className="mt-1 whitespace-pre-line text-sm text-gray-600">{item.description}</p>
              </div>
            )}
          </div>
        )}
      </SlideOverBody>
      {tab === 'comments' && (
        <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4">
          <CommentComposer people={team} meId={me?.id} onSend={thread.send} />
        </div>
      )}
      {item.access?.canRespond && !(tab === 'detail' && editingDetails) && (
        <div className="flex-shrink-0 border-t border-gray-100">
          <AppointmentResponseBar current={item.myResponse} onRespond={respond} />
        </div>
      )}
      {/* While editing, a status change or a delete would drop the form. */}
      {!(tab === 'detail' && editingDetails) && (
      <div className="flex-shrink-0 border-t border-gray-100">
        <SlideOverFooter>
          <div className="mr-auto flex items-center gap-2">
            {!location.pathname.startsWith('/planning') && (
              <CardIconButton
                label={text('seeInPlanning')}
                icon={<CalendarDaysIcon wh={18} color="#374151" />}
                onClick={() => {
                  close();
                  navigate(toPlace ? '/planning?placer=1' : `/planning?date=${isoDay(start)}`);
                }}
              />
            )}
            {/* One held, not held or canceled is reopened before it is changed. */}
            {canEditDetails && editable && !editingDetails && tab === 'detail' && (
              <CardIconButton
                label={text('edit')}
                icon={<Pencil wh={18} color="#374151" />}
                onClick={() => setEditing(true)}
              />
            )}
            {canEdit && (
              <CardIconButton
                label={actionText(removeKey)}
                icon={running === removeKey ? <Spinner /> : <TrashIcon wh={18} color="#374151" />}
                disabled={Boolean(running)}
                onClick={() =>
                  item.recurring && !item.visitId
                    ? setDeleteScopeOpen(true)
                    : act(
                        () => call(crmApi.removeAppointment, { id: item.id }),
                        (removed) => text(removed?.visitBack ? 'unplaced' : 'deleted'),
                        removeKey,
                      )
                }
              />
            )}
          </div>
          {actions}
        </SlideOverFooter>
      </div>
      )}
      <ScopeDialog
        open={deleteScopeOpen}
        action="delete"
        onCancel={() => setDeleteScopeOpen(false)}
        // Every scope deletes the occurrence shown: act closes the panel. Open
        // until the answer, its button reading « Suppression… ».
        onConfirm={(scope) =>
          act(() => call(crmApi.removeAppointment, { id: item.id, scope }), text('deleted'), 'delete').finally(() =>
            setDeleteScopeOpen(false),
          )
        }
      />
    </>
  );
};

export default AppointmentPanel;
